// src/layouts/annotator/exams/index.js
// Annotator — My Exams (A1 or A2) with tabs: Pending | In Progress | Submitted
// Columns change per tab; clicking Exam ID opens annotation.js

import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "hooks/useTranslation";

import { getStudentExams, getAnnotatorSampleCount } from "services/sampleService";

// Adjust to your routing
const ANNOTATION_ROUTE = "/annotator/exams/annotation";

// Per-annotator status filter sent to the backend for each tab
const TAB_MY_STATUS = {
  0: "Pending",
  1: "In Progress",
  2: "In Review,Completed",
};

/* --- Cell Component with PropTypes (no ESLint noise) --- */
function ExamIdCell({ row, onOpen }) {
  const { t } = useTranslation();
  const examId = row?.original?.exam_id || "";
  const hasRemark =
    Boolean(row?.original?.has_remark) &&
    (row?.original?.my_status || "").toLowerCase() === "in progress";
  return (
    <Box display="flex" alignItems="center">
      <Link component="button" variant="body2" onClick={() => onOpen(examId)} underline="hover">
        {examId}
      </Link>
      {hasRemark && (
        <Chip
          label={t("annotator.exams.remarksReceived", "Remarks received")}
          size="small"
          color="warning"
          sx={{ ml: 1, fontWeight: 500, fontSize: "0.65rem", color: "#fff" }}
        />
      )}
    </Box>
  );
}
ExamIdCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      exam_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      has_remark: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
      my_status: PropTypes.string,
    }).isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};

function AnnotatorExamsIndex() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Get logged-in annotator id (adjust to your auth source if needed)
  const getAnnotatorId = () => {
    // Try a few common keys
    const candidates = [localStorage.getItem("user_id")].filter(Boolean);
    if (candidates.length) {
      const v = candidates[0];
      const n = Number(v);
      return Number.isNaN(n) ? v : n;
    }
    return null;
  };
  const annotatorId = getAnnotatorId();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ page: 1, limit: 25 });
  // Per-tab counts for the tab badges (fetched via getAnnotatorSampleCount)
  const [counts, setCounts] = useState({ 0: 0, 1: 0, 2: 0 });
  // Tab state: 0: Pending, 1: In Progress, 2: Submitted
  const [tab, setTab] = useState(0);

  const handleOpenExam = useCallback(
    (currentExamId) => {
      const url = `${ANNOTATION_ROUTE}?exam_id=${encodeURIComponent(currentExamId)}`;
      navigate(url);
    },
    [navigate]
  );

  // Build columns per tab
  const columnsPendingInProgress = useMemo(
    () => [
      {
        Header: t("annotator.exams.table.columns.sessionId", "Sample ID"),
        accessor: "exam_id",
        align: "left",
        Cell: (p) => <ExamIdCell row={p.row} onOpen={handleOpenExam} />,
      },
      {
        Header: t("annotator.exams.table.columns.assignedOn", "Assigned On"),
        accessor: "my_assigned_on",
        align: "left",
      },
    ],
    [handleOpenExam, t]
  );

  const columnsSubmitted = useMemo(
    () => [
      {
        Header: t("annotator.exams.table.columns.sessionId", "Sample ID"),
        accessor: "exam_id",
        align: "left",
        Cell: (p) => <ExamIdCell row={p.row} onOpen={handleOpenExam} />,
      },
      {
        Header: t("annotator.exams.table.columns.submittedOn", "Submitted On"),
        accessor: "my_submitted_on",
        align: "left",
      },
    ],
    [handleOpenExam, t]
  );

  const columns = tab === 2 ? columnsSubmitted : columnsPendingInProgress;

  const fetchMyExams = async (nextTab = tab, nextFilters = filters) => {
    if (!annotatorId) {
      console.error("Annotator ID not found; ensure user is logged in and userId is available.");
      setRows([]);
      setTotal(0);
      return;
    }
    try {
      setLoading(true);
      // One server-paginated call: exams where I'm annotator1 OR annotator2,
      // filtered by MY status for the active tab.
      const resp = await getStudentExams({
        annotator_id: annotatorId,
        my_status: TAB_MY_STATUS[nextTab],
        page: nextFilters.page,
        limit: nextFilters.limit,
      });

      // Map and compute per-annotator fields
      const mapped = (resp.data || []).map((e) => {
        const isA1 = String(e.annotator1_id ?? "") === String(annotatorId);
        const isA2 = String(e.annotator2_id ?? "") === String(annotatorId);

        const myStatus = isA1 ? e.annotator1_status : isA2 ? e.annotator2_status : e.status;
        const assignedRaw = isA1
          ? e.annotator1_assigned_on
          : isA2
          ? e.annotator2_assigned_on
          : null;
        const submittedRaw = isA1
          ? e.annotator1_submitted_on
          : isA2
          ? e.annotator2_submitted_on
          : null;

        const fmt = (d) => (d ? new Date(d).toLocaleString() : "");

        return {
          exam_id: e.exam_id ?? "",
          student_id: e.student_id ?? "",
          student_name: e.student_name ?? "",
          my_status: myStatus || "",
          my_assigned_on: fmt(assignedRaw),
          my_submitted_on: fmt(submittedRaw),
          has_remark: Number(e.has_remark) || 0,

          // keep for display/debug if needed
          status: e.status ?? "",
          annotator1_id: e.annotator1_id ?? null,
          annotator2_id: e.annotator2_id ?? null,
          annotator1_name: e.annotator1_name ?? (e.annotator1_id ? `#${e.annotator1_id}` : ""),
          annotator2_name: e.annotator2_name ?? (e.annotator2_id ? `#${e.annotator2_id}` : ""),
          annotator1_status: e.annotator1_status ?? "",
          annotator2_status: e.annotator2_status ?? "",
          start_time: e.start_time ? new Date(e.start_time).toLocaleString() : "",
          end_time: e.end_time ? new Date(e.end_time).toLocaleString() : "",
        };
      });

      setRows(mapped);
      setTotal(resp.total_records ?? mapped.length);
    } catch (err) {
      console.error("fetchMyExams error:", err);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Tab badge counts from the lightweight count endpoint (no row fetch)
  const fetchCounts = async () => {
    if (!annotatorId) return;
    try {
      const { data_summary } = await getAnnotatorSampleCount(annotatorId);
      const next = { 0: 0, 1: 0, 2: 0 };
      (data_summary || []).forEach((item) => {
        const s = (item.status || "").toLowerCase();
        const n = Number(item.count) || 0;
        if (s === "pending") next[0] += n;
        else if (s === "in progress") next[1] += n;
        else if (s === "in review" || s === "completed") next[2] += n;
      });
      setCounts(next);
    } catch (err) {
      console.error("fetchCounts error:", err);
    }
  };

  useEffect(() => {
    fetchMyExams();
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (newTab) => {
    if (newTab === tab) return;
    const nextFilters = { ...filters, page: 1 };
    setTab(newTab);
    setFilters(nextFilters);
    fetchMyExams(newTab, nextFilters);
  };

  const onPageChange = (newPage) => {
    const nextFilters = { ...filters, page: newPage };
    setFilters(nextFilters);
    fetchMyExams(tab, nextFilters);
  };

  // Tab definitions with translations
  const tabDefs = [
    {
      key: 0,
      label: t("annotator.exams.tabs.pending", "Pending"),
      count: counts[0],
      barColor: "#e53935",
    },
    {
      key: 1,
      label: t("annotator.exams.tabs.inProgress", "In Progress"),
      count: counts[1],
      barColor: "#1d57dd",
    },
    {
      key: 2,
      label: t("annotator.exams.tabs.submitted", "Completed"),
      count: counts[2],
      barColor: "#43a047",
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                p: 2,
                border: "1px solid #e0e0e0",
                boxShadow: 2,
                borderRadius: 2,
                backgroundColor: "#fff",
              }}
            >
              {/* --- Custom tab bar with color-coded bottom bars --- */}
              <Box
                role="tablist"
                aria-label={t("annotator.exams.tabs.ariaLabel", "annotator exam status tabs")}
                display="flex"
                gap={1}
                mb={2}
                flexWrap="wrap"
                alignItems="center"
              >
                <MDTypography mr={1} fontWeight="bold">
                  {t("annotator.exams.title", "Status")} :
                </MDTypography>

                {tabDefs.map((t) => {
                  const isActive = tab === t.key;

                  return (
                    <Button
                      key={t.key}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => handleTabChange(t.key)}
                      variant="outlined"
                      sx={{
                        borderRadius: 1,
                        minWidth: 120,
                        textTransform: "none",
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        color: "#111",
                        border: "1px solid #ccc",
                        background: "#fff",
                        padding: "8px 18px",
                        boxShadow: isActive
                          ? `0 4px 0 0 ${t.barColor}` // ACTIVE bottom bar (colored)
                          : "0 4px 0 0 #ccc", // DEFAULT bottom bar
                        transition: "all 120ms ease",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: isActive ? `0 6px 0 0 ${t.barColor}` : "0 6px 0 0 #bbb",
                        },
                      }}
                    >
                      {t.label}
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          fontWeight: 600,
                          color: "#555",
                          background: "#f2f4f6",
                          px: 1,
                          borderRadius: "6px",
                          fontSize: "0.85rem",
                        }}
                      >
                        {t.count}
                      </Box>
                    </Button>
                  );
                })}
              </Box>

              <Card
                sx={{
                  boxShadow: 0,
                  borderRadius: 2,
                  overflow: "hidden",
                  backgroundColor: "#f9fafe",
                  border: "1px solid #e0e0e0",
                }}
              >
                <MDBox>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted
                    canSearch
                    showTotalEntries
                    totalRecords={total}
                    entriesPerPage={{
                      defaultValue: 25,
                      entries: [25, 50, 100],
                      onChange: (value) => {
                        const next = { page: 1, limit: value };
                        setFilters(next);
                        fetchMyExams(tab, next);
                      },
                    }}
                    pagination={{
                      variant: "gradient",
                      color: "info",
                      page: filters.page - 1,
                      onPageChange: (_, newPage) => onPageChange(newPage + 1),
                    }}
                    noEndBorder={false}
                  />
                </MDBox>
              </Card>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AnnotatorExamsIndex;
